import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  ArrowBack,
  Assessment,
  CalendarToday,
  TrendingUp,
  Receipt,
  Business,
  GetApp,
  ArrowUpward,
  ArrowDownward,
  UnfoldMore,
  AttachMoney,
  LocalShipping,
} from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { Chart } from "react-google-charts";
import fetchClient from "../../../api/fetchClient";
import Loader from "../../../components/Loader";
import message from "../../../helper/message";
import { formatAbbrValue } from "../../../helper/formatValue";
import {
  Container,
  ModernHeader,
  HeaderLeft,
  HeaderContent,
  PageTitle,
  PageSubtitle,
  ResultsCard,
  getTheme,
} from "../../../components/common";

const theme = getTheme("senior");
const PRIMARY = theme.primary; // #84cc16
const PRIMARY_DARK = theme.primaryDark; // #65a30d

const DOC_TYPE_COLORS = [PRIMARY, "#3498db", "#e67e22", "#8e44ad", "#27ae60", "#e74c3c", "#1abc9c"];
const CURRENCY_COLORS = [PRIMARY, "#3498db", "#e67e22", "#8e44ad", "#e74c3c", "#1abc9c"];

const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const MONTHS_FULL = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const YEAR_OPTIONS = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i + 1).reverse();

const formatCurrency = (value, currency) => {
  try {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: currency || "BRL" }).format(value || 0);
  } catch {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);
  }
};

const formatAbbrCurrency = (value, currency) => {
  const cur = currency || "BRL";
  const abs = Math.abs(value || 0);
  let abbreviated;
  if (abs >= 1_000_000_000) {
    abbreviated = ((value || 0) / 1_000_000_000).toFixed(1).replace(".", ",") + "B";
  } else if (abs >= 1_000_000) {
    abbreviated = ((value || 0) / 1_000_000).toFixed(1).replace(".", ",") + "M";
  } else if (abs >= 1_000) {
    abbreviated = ((value || 0) / 1_000).toFixed(1).replace(".", ",") + "K";
  } else {
    abbreviated = (value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  try {
    const symbol = new Intl.NumberFormat("pt-BR", { style: "currency", currency: cur })
      .formatToParts(0)
      .find((p) => p.type === "currency")?.value || cur;
    return `${symbol} ${abbreviated}`;
  } catch {
    return `R$ ${abbreviated}`;
  }
};

function DashboardMetricsWMS() {
  const history = useHistory();

  // Data
  const [groups, setGroups] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Filters
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedDocType, setSelectedDocType] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");

  // Chart toggles
  const [donutMode, setDonutMode] = useState("count");
  const [barMode, setBarMode] = useState("count");

  // Table
  const [sortCol, setSortCol] = useState("month");
  const [sortDir, setSortDir] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Load groups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const { data } = await fetchClient.get("/wms/getAllGroups");
        setGroups(data || []);
      } catch (e) {
        console.error("Erro ao carregar grupos WMS", e);
      }
    };
    fetchGroups();
  }, []);

  // Auto-fetch when group or year changes
  useEffect(() => {
    if (selectedGroup && selectedYear) fetchMetrics();
  }, [selectedGroup, selectedYear]);

  const fetchMetrics = useCallback(async () => {
    if (!selectedGroup) return;
    try {
      setIsLoading(true);
      const params = new URLSearchParams({ grupoId: selectedGroup, year: selectedYear });
      const { data } = await fetchClient.get(`/wms/metrics?${params}`);
      setMetrics(Array.isArray(data) ? data : []);
      setHasFetched(true);
      setSelectedDocType("");
      setSelectedCurrency("");
      setCurrentPage(1);
    } catch (e) {
      const msg = e.response?.data?.message || "Erro ao buscar métricas WMS";
      message.error(msg);
      setMetrics([]);
      setHasFetched(true);
    } finally {
      setIsLoading(false);
    }
  }, [selectedGroup, selectedYear]);

  // Derived: unique docTypes, currencies
  const uniqueDocTypes = useMemo(
    () => [...new Set(metrics.map((m) => m.docType).filter(Boolean))].sort(),
    [metrics]
  );

  const uniqueCurrencies = useMemo(
    () => [...new Set(metrics.map((m) => m.currency).filter(Boolean))].sort(),
    [metrics]
  );

  // Filtered metrics (charts + table)
  const filteredMetrics = useMemo(() => {
    let result = metrics;
    if (selectedMonth) result = result.filter((m) => m.month === parseInt(selectedMonth, 10));
    if (selectedDocType) result = result.filter((m) => m.docType === selectedDocType);
    if (selectedCurrency) result = result.filter((m) => m.currency === selectedCurrency);
    return result;
  }, [metrics, selectedMonth, selectedDocType, selectedCurrency]);

  // KPIs — grouped by currency, never sum across currencies
  const kpisByCurrency = useMemo(() => {
    const byCurrency = {};
    filteredMetrics.forEach((m) => {
      const curr = m.currency || "BRL";
      if (!byCurrency[curr]) byCurrency[curr] = { transactionCount: 0, totalValue: 0 };
      byCurrency[curr].transactionCount += m.transactionCount || 0;
      byCurrency[curr].totalValue += m.totalValue || 0;
    });
    return Object.entries(byCurrency)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([currency, data]) => ({ currency, ...data }));
  }, [filteredMetrics]);

  const isMultiCurrency = kpisByCurrency.length > 1;

  // Variation (last two months)
  const kpiVariation = useMemo(() => {
    const pct = (curr, prev) => (prev > 0 ? ((curr - prev) / prev) * 100 : null);
    if (!metrics.length) return { count: null, value: null };
    const grouped = {};
    metrics.forEach((m) => {
      const mo = m.month;
      if (!grouped[mo]) grouped[mo] = { transactionCount: 0, totalValue: 0 };
      grouped[mo].transactionCount += m.transactionCount || 0;
      grouped[mo].totalValue += m.totalValue || 0;
    });
    const months = Object.keys(grouped).map(Number).sort((a, b) => a - b);
    if (months.length < 2) return { count: null, value: null };
    const last = grouped[months[months.length - 1]];
    const prev = grouped[months[months.length - 2]];
    return {
      count: pct(last.transactionCount, prev.transactionCount),
      value: pct(last.totalValue, prev.totalValue),
    };
  }, [metrics]);

  // Evolution chart data
  const evolutionChartData = useMemo(() => {
    if (!filteredMetrics.length) return [["Mês", "Documentos", "Valor Total"]];
    const grouped = {};
    filteredMetrics.forEach((m) => {
      if (!grouped[m.month]) grouped[m.month] = { count: 0, value: 0 };
      grouped[m.month].count += m.transactionCount || 0;
      grouped[m.month].value += m.totalValue || 0;
    });
    const chartData = [["Mês", "Documentos", "Valor Total"]];
    Object.keys(grouped)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((mo) => chartData.push([MONTHS[mo - 1], grouped[mo].count, grouped[mo].value]));
    return chartData;
  }, [filteredMetrics]);

  // Donut chart data
  const donutChartData = useMemo(() => {
    if (!filteredMetrics.length) return [["Tipo", "Valor"]];
    const grouped = {};
    filteredMetrics.forEach((m) => {
      const label = m.docType || "Outros";
      if (!grouped[label]) grouped[label] = { count: 0, value: 0 };
      grouped[label].count += m.transactionCount || 0;
      grouped[label].value += m.totalValue || 0;
    });
    if (donutMode === "value") {
      const chartData = [["Tipo", "Valor", { role: "tooltip" }]];
      Object.entries(grouped)
        .filter(([, d]) => d.value > 0)
        .forEach(([label, d]) => chartData.push([label, d.value, `${label}: ${formatAbbrValue(d.value, true)}`]));
      return chartData;
    }
    const chartData = [["Tipo", "Documentos"]];
    Object.entries(grouped)
      .filter(([, d]) => d.count > 0)
      .forEach(([label, d]) => chartData.push([label, d.count]));
    return chartData;
  }, [filteredMetrics, donutMode]);

  // Bar chart: groups x docType
  const barChartData = useMemo(() => {
    if (!filteredMetrics.length) return [["Grupo", "Documentos"]];
    const docTypes = [...new Set(filteredMetrics.map((m) => m.docType))].sort();
    const grouped = {};
    filteredMetrics.forEach((m) => {
      const grp = m.grupoId || "N/A";
      if (!grouped[grp]) grouped[grp] = {};
      if (!grouped[grp][m.docType]) grouped[grp][m.docType] = { count: 0, value: 0 };
      grouped[grp][m.docType].count += m.transactionCount || 0;
      grouped[grp][m.docType].value += m.totalValue || 0;
    });
    const headers = ["Grupo", ...docTypes];
    const chartData = [headers];
    Object.entries(grouped)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .forEach(([grp, byType]) => {
        const row = [grp];
        docTypes.forEach((dt) =>
          row.push(barMode === "count" ? byType[dt]?.count || 0 : byType[dt]?.value || 0)
        );
        chartData.push(row);
      });
    return chartData;
  }, [filteredMetrics, barMode]);

  // Table sorting
  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
    setCurrentPage(1);
  };

  const sortedTableData = useMemo(() => {
    return filteredMetrics.slice().sort((a, b) => {
      let va, vb;
      switch (sortCol) {
        case "grupo": va = a.grupoId || ""; vb = b.grupoId || ""; break;
        case "docType": va = a.docType || ""; vb = b.docType || ""; break;
        case "currency": va = a.currency || ""; vb = b.currency || ""; break;
        case "month": va = a.month; vb = b.month; break;
        case "count": va = a.transactionCount || 0; vb = b.transactionCount || 0; break;
        case "value": va = a.totalValue || 0; vb = b.totalValue || 0; break;
        default: va = a.month; vb = b.month;
      }
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? va - vb : vb - va;
    });
  }, [filteredMetrics, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedTableData.length / rowsPerPage));
  const paginatedData = sortedTableData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  useEffect(() => { setCurrentPage(1); }, [filteredMetrics, rowsPerPage]);

  // CSV Export
  const exportCSV = () => {
    const headers = ["Grupo", "Tipo Doc", "Moeda", "Mês", "Ano", "Documentos", "Valor Total"];
    const rows = sortedTableData.map((m) => [
      m.grupoId || "-",
      m.docType || "-",
      m.currency || "BRL",
      MONTHS_FULL[m.month - 1] || m.month,
      m.year || selectedYear,
      m.transactionCount || 0,
      (m.totalValue || 0).toFixed(2).replace(".", ","),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wms-metricas-${selectedGroup}-${selectedYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <UnfoldMore style={{ fontSize: 14, opacity: 0.4 }} />;
    return sortDir === "asc"
      ? <ArrowUpward style={{ fontSize: 14, color: PRIMARY }} />
      : <ArrowDownward style={{ fontSize: 14, color: PRIMARY }} />;
  };

  const showData = !isLoading && hasFetched;
  const singleKpi = kpisByCurrency[0] || { transactionCount: 0, totalValue: 0, currency: "BRL" };

  return (
    <Container>
      <ModernHeader>
        <HeaderLeft>
          <BackBtn onClick={() => history.goBack()}>
            <ArrowBack />
          </BackBtn>
          <HeaderContent>
            <PageTitle>
              <LocalShipping style={{ marginRight: "12px", fontSize: "32px" }} />
              Dashboard WMS — Métricas
            </PageTitle>
            <PageSubtitle>Análise de documentos por grupo, tipo e período</PageSubtitle>
          </HeaderContent>
        </HeaderLeft>
      </ModernHeader>

      <ResultsCard>
        {/* ── Filtros ─────────────────────────── */}
        <FiltersSection>
          <FiltersRow>
            {/* Grupo */}
            <FilterGroup>
              <FilterLabel>
                <Business style={{ fontSize: 15 }} /> Grupo
              </FilterLabel>
              <FSelect
                value={selectedGroup}
                onChange={(e) => { setSelectedGroup(e.target.value); }}
              >
                <option value="">Selecione um grupo</option>
                <option value="all">Todos os grupos</option>
                {groups.map((g) => (
                  <option key={g.id || g.grupoId} value={g.id || g.grupoId}>
                    {g.name || g.id || g.grupoId}
                  </option>
                ))}
              </FSelect>
            </FilterGroup>

            {/* Ano */}
            <FilterGroup>
              <FilterLabel>
                <CalendarToday style={{ fontSize: 15 }} /> Ano
              </FilterLabel>
              <FSelect value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
              </FSelect>
            </FilterGroup>

            {/* Mês */}
            <FilterGroup>
              <FilterLabel>
                <CalendarToday style={{ fontSize: 15 }} /> Mês
              </FilterLabel>
              <FSelect value={selectedMonth} onChange={(e) => { setSelectedMonth(e.target.value); setCurrentPage(1); }}>
                <option value="">Todos os meses</option>
                {MONTHS.map((m, i) => {
                  if (selectedYear === new Date().getFullYear() && i + 1 > new Date().getMonth() + 1) return null;
                  return <option key={i + 1} value={String(i + 1)}>{m}</option>;
                })}
              </FSelect>
            </FilterGroup>

            {/* Tipo de Documento */}
            <FilterGroup>
              <FilterLabel>
                <Receipt style={{ fontSize: 15 }} /> Tipo Doc
              </FilterLabel>
              <FSelect
                value={selectedDocType}
                onChange={(e) => { setSelectedDocType(e.target.value); setCurrentPage(1); }}
                disabled={!hasFetched || uniqueDocTypes.length === 0}
              >
                <option value="">Todos</option>
                {uniqueDocTypes.map((dt) => <option key={dt} value={dt}>{dt}</option>)}
              </FSelect>
            </FilterGroup>

            {/* Moeda */}
            {uniqueCurrencies.length > 1 && (
              <FilterGroup>
                <FilterLabel>
                  <AttachMoney style={{ fontSize: 15 }} /> Moeda
                </FilterLabel>
                <FSelect
                  value={selectedCurrency}
                  onChange={(e) => { setSelectedCurrency(e.target.value); setCurrentPage(1); }}
                  disabled={!hasFetched}
                >
                  <option value="">Todas as moedas</option>
                  {uniqueCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
                </FSelect>
              </FilterGroup>
            )}
          </FiltersRow>
        </FiltersSection>

        {isLoading && <Loader />}

        {!isLoading && !selectedGroup && (
          <EmptyState>
            <LocalShipping style={{ fontSize: 64, color: "#cbd5e1", marginBottom: 16 }} />
            <EmptyTitle>Selecione um grupo</EmptyTitle>
            <EmptyText>Escolha um grupo WMS para visualizar as métricas.</EmptyText>
          </EmptyState>
        )}

        {showData && (
          <>
            {/* ── KPIs ──────────────────────────────── */}
            {isMultiCurrency ? (
              <KPIGrid>
                {kpisByCurrency.map((k, idx) => {
                  const color = CURRENCY_COLORS[idx % CURRENCY_COLORS.length];
                  return (
                    <CurrencyKPICard key={k.currency} color={color}>
                      <CurrencyKPIHeader color={color}>
                        <AttachMoney style={{ fontSize: 16 }} />
                        {k.currency} — {k.transactionCount.toLocaleString("pt-BR")} documentos
                      </CurrencyKPIHeader>
                      <CurrencyKPIValue>{formatAbbrCurrency(k.totalValue, k.currency)}</CurrencyKPIValue>
                      <CurrencyKPIDetail>Valor Total</CurrencyKPIDetail>
                    </CurrencyKPICard>
                  );
                })}
              </KPIGrid>
            ) : (
              <KPIGrid>
                <KPICard color={PRIMARY}>
                  <KPIIcon color={PRIMARY}><Receipt /></KPIIcon>
                  <KPIContent>
                    <KPIValue>{formatAbbrValue(singleKpi.transactionCount)}</KPIValue>
                    <KPILabel>Total Documentos{singleKpi.currency ? ` (${singleKpi.currency})` : ""}</KPILabel>
                    {!selectedMonth && kpiVariation.count !== null && (
                      <VarBadge positive={kpiVariation.count >= 0}>
                        {kpiVariation.count >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.count).toFixed(1)}% vs ant.
                      </VarBadge>
                    )}
                  </KPIContent>
                </KPICard>

                <KPICard color="#27ae60">
                  <KPIIcon color="#27ae60"><TrendingUp /></KPIIcon>
                  <KPIContent>
                    <KPIValue>{formatAbbrCurrency(singleKpi.totalValue, singleKpi.currency)}</KPIValue>
                    <KPILabel>Valor Total</KPILabel>
                    {!selectedMonth && kpiVariation.value !== null && (
                      <VarBadge positive={kpiVariation.value >= 0}>
                        {kpiVariation.value >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.value).toFixed(1)}% vs ant.
                      </VarBadge>
                    )}
                  </KPIContent>
                </KPICard>

                <KPICard color="#3498db">
                  <KPIIcon color="#3498db"><Assessment /></KPIIcon>
                  <KPIContent>
                    <KPIValue>{uniqueDocTypes.length}</KPIValue>
                    <KPILabel>Tipos de Documento</KPILabel>
                  </KPIContent>
                </KPICard>
              </KPIGrid>
            )}

            {metrics.length === 0 ? (
              <EmptyState>
                <LocalShipping style={{ fontSize: 64, color: "#cbd5e1", marginBottom: 16 }} />
                <EmptyTitle>Nenhum dado encontrado</EmptyTitle>
                <EmptyText>Nenhuma métrica encontrada para o período selecionado.</EmptyText>
              </EmptyState>
            ) : (
              <>
                {/* ── Evolução Mensal ─────────────────── */}
                {!selectedMonth && (
                  <ChartCard>
                    <ChartCardHeader>
                      <ChartCardTitle>Evolução Mensal — {selectedYear}</ChartCardTitle>
                    </ChartCardHeader>
                    <Chart
                      chartType="ComboChart"
                      width="100%"
                      height="400px"
                      data={evolutionChartData}
                      options={{
                        chartArea: { width: "75%", height: "70%" },
                        hAxis: { title: "Mês" },
                        seriesType: "bars",
                        series: {
                          0: { color: PRIMARY, targetAxisIndex: 0 },
                          1: { type: "line", color: "#27ae60", targetAxisIndex: 1, lineWidth: 3, pointSize: 7 },
                        },
                        vAxes: {
                          0: { title: "Documentos", minValue: 0, gridlines: { color: "#f0f0f0" } },
                          1: { title: "Valor Total", format: "short", gridlines: { color: "#f5f5f5" } },
                        },
                        legend: { position: "top" },
                        animation: { startup: true, duration: 1000, easing: "out" },
                      }}
                    />
                  </ChartCard>
                )}

                {/* ── Donut + Barras ──────────────────── */}
                <ChartsRow>
                  {donutChartData.length > 1 && (
                    <ChartCard style={{ marginBottom: 0 }}>
                      <ChartCardHeader>
                        <ChartCardTitle>Distribuição por Tipo</ChartCardTitle>
                        <ToggleGroup>
                          <ToggleBtn active={donutMode === "count"} onClick={() => setDonutMode("count")}>Documentos</ToggleBtn>
                          <ToggleBtn active={donutMode === "value"} onClick={() => setDonutMode("value")}>Valor</ToggleBtn>
                        </ToggleGroup>
                      </ChartCardHeader>
                      <Chart
                        chartType="PieChart"
                        width="100%"
                        height="320px"
                        data={donutChartData}
                        options={{
                          chartArea: { width: "90%", height: "80%" },
                          colors: DOC_TYPE_COLORS,
                          legend: { position: "bottom", textStyle: { fontSize: 12 } },
                          pieHole: 0.4,
                          animation: { startup: true, duration: 1000, easing: "out" },
                        }}
                      />
                    </ChartCard>
                  )}

                  {barChartData.length > 1 && selectedGroup === "all" && (
                    <ChartCard style={{ marginBottom: 0 }}>
                      <ChartCardHeader>
                        <ChartCardTitle>Comparativo por Grupo</ChartCardTitle>
                        <ToggleGroup>
                          <ToggleBtn active={barMode === "count"} onClick={() => setBarMode("count")}>Documentos</ToggleBtn>
                          <ToggleBtn active={barMode === "value"} onClick={() => setBarMode("value")}>Valor</ToggleBtn>
                        </ToggleGroup>
                      </ChartCardHeader>
                      <Chart
                        chartType="ColumnChart"
                        width="100%"
                        height="320px"
                        data={barChartData}
                        options={{
                          chartArea: { width: "75%", height: "65%" },
                          hAxis: { title: "Grupo" },
                          vAxis: {
                            title: barMode === "count" ? "Documentos" : "Valor",
                            minValue: 0,
                            ...(barMode === "value" ? { format: "short" } : {}),
                          },
                          colors: DOC_TYPE_COLORS,
                          isStacked: false,
                          legend: { position: "top", textStyle: { fontSize: 11 } },
                          animation: { startup: true, duration: 1000, easing: "out" },
                        }}
                      />
                    </ChartCard>
                  )}
                </ChartsRow>

                {/* ── Tabela ──────────────────────────── */}
                <TableCard>
                  <TableCardHeader>
                    <TableTitle>Detalhamento</TableTitle>
                    <ExportBtn onClick={exportCSV} disabled={sortedTableData.length === 0}>
                      <GetApp style={{ fontSize: 17 }} /> Exportar CSV
                    </ExportBtn>
                  </TableCardHeader>
                  <TableWrap>
                    <DataTable>
                      <thead>
                        <tr>
                          <SortHeader onClick={() => handleSort("grupo")}>
                            Grupo <SortIcon col="grupo" />
                          </SortHeader>
                          <SortHeader onClick={() => handleSort("docType")}>
                            Tipo Doc <SortIcon col="docType" />
                          </SortHeader>
                          <SortHeader onClick={() => handleSort("currency")}>
                            Moeda <SortIcon col="currency" />
                          </SortHeader>
                          <SortHeader onClick={() => handleSort("month")}>
                            Mês <SortIcon col="month" />
                          </SortHeader>
                          <SortHeader align="right" onClick={() => handleSort("count")}>
                            Documentos <SortIcon col="count" />
                          </SortHeader>
                          <SortHeader align="right" onClick={() => handleSort("value")}>
                            Valor Total <SortIcon col="value" />
                          </SortHeader>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.length === 0 ? (
                          <tr>
                            <TD colSpan={6} style={{ textAlign: "center", padding: "32px", color: "#9ca3af" }}>
                              Nenhum registro encontrado para o período selecionado.
                            </TD>
                          </tr>
                        ) : (
                          paginatedData.map((m, idx) => {
                            const curr = m.currency || "BRL";
                            return (
                              <TRow key={idx}>
                                <TD><strong>{m.grupoId || "-"}</strong></TD>
                                <TD>
                                  <DocTypeBadge>{m.docType || "-"}</DocTypeBadge>
                                </TD>
                                <TD>
                                  <CurrencyBadge>{curr}</CurrencyBadge>
                                </TD>
                                <TD>{MONTHS_FULL[m.month - 1] || "-"}</TD>
                                <TD align="right">
                                  <Metric color={PRIMARY}>{(m.transactionCount || 0).toLocaleString("pt-BR")}</Metric>
                                </TD>
                                <TD align="right">
                                  <Metric color="#27ae60">{formatCurrency(m.totalValue, curr)}</Metric>
                                </TD>
                              </TRow>
                            );
                          })
                        )}
                      </tbody>
                    </DataTable>
                  </TableWrap>

                  <PaginationContainer>
                    <PaginationInfo>
                      Mostrando {sortedTableData.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1}–
                      {Math.min(currentPage * rowsPerPage, sortedTableData.length)} de {sortedTableData.length} registros
                    </PaginationInfo>
                    <PaginationControls>
                      <PaginationLabel>Linhas por página:</PaginationLabel>
                      <PaginationSelect value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </PaginationSelect>
                      <PageBtn disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Anterior</PageBtn>
                      <PageInfo>Página {currentPage} de {totalPages}</PageInfo>
                      <PageBtn disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Próxima</PageBtn>
                    </PaginationControls>
                  </PaginationContainer>
                </TableCard>
              </>
            )}
          </>
        )}
      </ResultsCard>
    </Container>
  );
}

export default DashboardMetricsWMS;

// ===== STYLED COMPONENTS =====

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const BackBtn = styled.button`
  background: ${theme.backButtonBg};
  border: none;
  border-radius: 12px;
  width: 42px; height: 42px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  color: ${PRIMARY};
  transition: all 0.3s ease;
  flex-shrink: 0;
  &:hover { background: ${theme.backButtonBgHover}; transform: translateX(-4px); }
`;

// Filters
const FiltersSection = styled.div`
  padding: 24px 0;
  margin-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const FiltersRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  font-family: "Mulish", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const FSelect = styled.select`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-family: "Mulish", sans-serif;
  background: #f9fafb;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  &:focus { outline: none; border-color: ${PRIMARY}; box-shadow: 0 0 0 3px ${theme.buttonShadow}; background: white; }
  &:hover { border-color: #d0d0d0; background: white; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// KPIs
const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const KPICard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #e8e8e8;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  animation: ${fadeIn} 0.5s ease;
  transition: all 0.3s ease;
  &:hover { transform: translateY(-4px); box-shadow: 0 8px 20px rgba(0,0,0,0.1); }
`;

const KPIIcon = styled.div`
  width: 56px; height: 56px;
  border-radius: 12px;
  background: ${(p) => `${p.color}18`};
  color: ${(p) => p.color};
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  svg { font-size: 28px; }
`;

const KPIContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const KPIValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
`;

const KPILabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
`;

const VarBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 4px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  font-family: "Mulish", sans-serif;
  background: ${(p) => p.positive ? "#27ae6015" : "#e74c3c15"};
  color: ${(p) => p.positive ? "#27ae60" : "#e74c3c"};
`;

// Multi-currency KPI
const CurrencyKPICard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #e8e8e8;
  border-left: 4px solid ${(p) => p.color};
  animation: ${fadeIn} 0.5s ease;
  transition: all 0.3s ease;
  &:hover { transform: translateY(-4px); box-shadow: 0 8px 20px rgba(0,0,0,0.1); }
`;

const CurrencyKPIHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => p.color};
  font-family: "Mulish", sans-serif;
  margin-bottom: 10px;
`;

const CurrencyKPIValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
  margin-bottom: 4px;
`;

const CurrencyKPIDetail = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
`;

// Charts
const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #e8e8e8;
  margin-bottom: 24px;
  animation: ${fadeIn} 0.5s ease;
`;

const ChartCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ChartCardTitle = styled.h4`
  font-size: 15px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  font-family: "Mulish", sans-serif;
`;

const ToggleGroup = styled.div`
  display: flex;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const ToggleBtn = styled.button`
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  font-family: "Mulish", sans-serif;
  border: none;
  cursor: pointer;
  background: ${(p) => p.active ? PRIMARY : "white"};
  color: ${(p) => p.active ? "white" : "#6b7280"};
  transition: all 0.2s ease;
  &:hover { background: ${(p) => p.active ? PRIMARY_DARK : "#f9fafb"}; }
`;

// Table
const TableCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #e8e8e8;
  animation: ${fadeIn} 0.5s ease;
`;

const TableCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const TableTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  font-family: "Mulish", sans-serif;
`;

const ExportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 2px solid ${PRIMARY};
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  font-family: "Mulish", sans-serif;
  background: white;
  color: ${PRIMARY};
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover:not(:disabled) { background: ${PRIMARY}; color: white; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const SortHeader = styled.th`
  text-align: ${(p) => p.align || "left"};
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e5e7eb;
  font-family: "Mulish", sans-serif;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  &:hover { color: ${PRIMARY}; }
`;

const TRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
  transition: background 0.2s ease;
  &:last-child { border-bottom: none; }
  &:hover { background: ${PRIMARY}0d; }
`;

const TD = styled.td`
  text-align: ${(p) => p.align || "left"};
  padding: 14px 16px;
  font-size: 14px;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
`;

const Metric = styled.span`
  display: inline-block;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  background: ${(p) => `${p.color}15`};
  color: ${(p) => p.color};
`;

const DocTypeBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${PRIMARY}18;
  color: ${PRIMARY_DARK};
  font-family: "Mulish", sans-serif;
  text-transform: capitalize;
`;

const CurrencyBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  background: rgba(52,152,219,0.1);
  color: #3498db;
  font-family: "Mulish", sans-serif;
  letter-spacing: 0.04em;
`;

// Pagination
const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0 0;
  margin-top: 16px;
  border-top: 1px solid #e5e7eb;
  flex-wrap: wrap;
  gap: 12px;
`;

const PaginationInfo = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PaginationLabel = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
`;

const PaginationSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  font-family: "Mulish", sans-serif;
  background: white;
  cursor: pointer;
  &:focus { outline: none; border-color: ${PRIMARY}; }
`;

const PageBtn = styled.button`
  padding: 7px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  font-family: "Mulish", sans-serif;
  background: white;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover:not(:disabled) { background: #f3f4f6; border-color: ${PRIMARY}; color: ${PRIMARY}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const PageInfo = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
`;

// Empty
const EmptyState = styled.div`
  text-align: center;
  padding: 64px 24px;
  animation: ${fadeIn} 0.5s ease;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 12px 0;
  font-family: "Mulish", sans-serif;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  font-family: "Mulish", sans-serif;
`;
